import { Row, Col } from "antd";
import {
    ObjectFieldTemplateProps,
    canExpand,
    descriptionId,
    getTemplate,
    getUiOptions,
    titleId,
} from "@rjsf/utils";
import TitleFieldTemplate from "../TitleFieldTemplate";

/** The `HorizontalFieldTemplate` is a template that renders the title horizontally next to the content
 * instead of above it. The title is positioned on the left side and the content on the right side.
 *
 * @param props - The `ObjectFieldTemplateProps` for this component
 */
export default function HorizontalFieldTemplate({
    description,
    title,
    properties,
    disabled,
    readonly,
    uiSchema,
    idSchema,
    schema,
    formData,
    onAddClick,
    registry,
    required,
}: ObjectFieldTemplateProps) {
    const uiOptions = getUiOptions(uiSchema);

    const DescriptionFieldTemplate = getTemplate(
        "DescriptionFieldTemplate",
        registry,
        uiOptions
    );
    // Button templates are not overridden in the uiSchema
    const {
        ButtonTemplates: { AddButton },
    } = registry.templates;

    return (
        <div className="w-full bg-white rounded-xl shadow-md pt-2 pb-4 px-6 border-t">
            <Row gutter={24} align="middle" className="min-h-[60px]">
                {/* Title Column - Fixed width on the left */}
                <Col span={24} className="flex items-center border-b border-solid border-gray-300 pb-2 mb-2">
                    {title && (
                        <TitleFieldTemplate
                            id={titleId(idSchema)}
                            title={title}
                            required={required}
                            description={typeof description === 'string' ? <span>{description}</span> : description}
                            depth={0}
                            schema={schema}
                        />
                    )}
                </Col>

                {/* Content Column - Flexible width on the right */}
                <Col span={24}>
                    {description && (
                        <DescriptionFieldTemplate
                            id={descriptionId(idSchema)}
                            description={description}
                            schema={schema}
                            uiSchema={uiSchema}
                            registry={registry}
                        />
                    )}

                    <div className="hnn-prestaties-grid">
                        {properties.map((element, index) => {
                            // Remove wrapping if the inner element is hidden
                            if (element.hidden) {
                                return element.content;
                            }
                            // only show the label for the first element, make it look like a table
                            // NOTE: This is a workaround to avoid showing the label for the first element
                            // @ts-expect-error type definition mismatch
                            return index === 0 && title.props.title?.toLowerCase().startsWith("milieu-impact") ? <div key={index}>
                                {element.content}
                            </div> : <div key={index} className="grid-item-no-label">
                                {element.content}
                            </div>
                        })}
                    </div>

                    {canExpand(schema, uiSchema, formData) && (
                        <div className="mt-4 text-right">
                            <AddButton
                                className="object-property-expand"
                                onClick={onAddClick(schema)}
                                disabled={disabled || readonly}
                                uiSchema={uiSchema}
                                registry={registry}
                            />
                        </div>
                    )}
                </Col>
            </Row>
        </div>
    );
}
